"use client";
import { PlusIcon, MinusIcon } from "lucide-react";
import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/hongkong-setup-v2/ui/badge";

const leftColumnFaqs = [
  {
    id: "item-1",
    question: "What exactly is included in your Hong Kong PSP\nsetup service?",
    answer:
"Our complete Hong Kong PSP package includes: Hong Kong Limited company formation, registered business address, Airwallex multi-currency business account, Ocean Payment merchant account, PayPal HK verified account, full compliance documentation, ongoing support until all PSPs are active. Everything needed for unlimited payment processing."
  },
  {
    id: "item-2",
    question:
      "How much does the Hong Kong PSP setup cost and what payment methods do you accept?",
    answer:"The complete Hong Kong PSP setup is $4,000 USD, payable via cryptocurrency, wire transfer, or existing payment processors. This one-time investment includes all company formation, banking, PSP activation, and compliance setup. No hidden fees or ongoing costs."
 },
  {
    id: "item-3",
    question: "How long does the complete Hong Kong PSP setup take?",
    answer:"Complete setup takes 2-4 weeks total: Hong Kong company formation (3-5 days), business address & compliance (2-3 days), Airwallex account opening (3-7 days), Ocean Payment activation (5-10 days), PayPal HK verification (2-5 days). We handle everything remotely - no need to visit Hong Kong."
  },
  {
    id: "item-4",
    question: "Why Hong Kong for PSP setup instead of other jurisdictions?",
    answer:"Hong Kong offers unique advantages: world-class banking infrastructure, stable regulatory environment, favorable business laws, strategic Asia-Pacific location, multiple currency support, and established relationships with global payment networks. It's the optimal jurisdiction for reliable, long-term payment processing."
 },
  {
    id: "item-5",
    question: "What ongoing support do you provide after setup?",
    answer:"We provide comprehensive post-setup support including: PSP account management guidance, compliance assistance, transaction monitoring setup, dispute resolution help, account optimization recommendations, and direct access to our team for any processing issues or questions."
    
 },
  {
    id: "item-6",
    question: "How do I get started with the Hong Kong\nPSP setup?",
    answer:"You can contact us directly on whatsapp +1 (786) 927-4708. We'll conduct a business assessment, explain the complete process, provide timeline expectations, and can begin Hong Kong company formation within 24-48 hours of payment confirmation."
 },
];

const rightColumnFaqs = [
  {
    id: "item-7",
    question:
      "Can you help if I've been banned by Stripe, PayPal, or other processors?",
    answer:"Absolutely! Our Hong Kong PSP setup is specifically designed for businesses experiencing random bans, holds, or shutdowns from traditional processors. We've helped 2500+ entrepreneurs escape payment processor restrictions and achieve stable, unlimited processing capacity."
 },
  {
    id: "item-8",
    question: "What processing volumes can the Hong Kong\nPSP handle?",
    answer:"Our Hong Kong PSP setup supports unlimited processing volumes with 99.8% uptime. Clients regularly process $500k-$5M+ monthly without holds, freezes, or volume restrictions. The infrastructure is designed for high-volume, high-risk businesses that traditional processors reject."
  },
  {
    id: "item-9",
    question: "What types of businesses can use Hong Kong PSP accounts?",
    answer:"Our Hong Kong PSP works for most business models including: e-commerce stores, digital marketing agencies, SaaS platforms, consulting services, affiliate marketing, online education, media buying, luxury brands, and other high-risk industries that traditional processors decline or restrict."
  },
  {
    id: "item-10",
    question: "Is the Hong Kong PSP setup completely legal and compliant?",
    answer:"Yes, absolutely. All Hong Kong company formations, banking relationships, and PSP accounts are established through proper legal channels with full regulatory compliance. We work with licensed Hong Kong corporate service providers and maintain all required documentation for transparency."
 },
  {
    id: "item-11",
    question: "Do you offer any guarantees on the Hong Kong PSP setup?",
    answer:"Yes, we offer a money-back guarantee. If we fail to deliver the complete Hong Kong PSP setup as promised due to issues on our end, we provide a full refund. Our 5+ years of experience and 2500+ successful setups demonstrate our proven track record."
 },
  {
    id: "item-12",
    question:
      "What documents do I need to provide for the Hong Kong\nPSP setup?",
    answer:"Required documents include: passport copy, business registration documents (if existing business), proof of address, business description/website, and basic KYC information. We'll guide you through the exact requirements and help prepare all necessary documentation for smooth processing."
 },
];

export const FrequentlyAskedQuestionsSection = () => {
  // Combine all FAQs for mobile single column
  const allFaqs = [...leftColumnFaqs, ...rightColumnFaqs];

  return (
    <section className="flex flex-col w-full max-w-[1400px] mx-auto gap-8 md:gap-10 lg:gap-14 px-4 sm:px-6 md:px-12 lg:px-20 xl:px-[120px] py-10 md:py-16 lg:py-[70px]">
      <header className="flex flex-col items-center w-full gap-3 md:gap-4">
        <Badge
          variant="outline"
          className="inline-flex items-center px-[15px] py-[5px] bg-[#ffffff01] rounded-[32px] border-[#14224d] shadow-[inset_0px_-7px_11px_#6fa6ff1f] backdrop-blur-[2px] backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(2px)_brightness(100%)]"
        >
          <span className="font-inter font-medium text-[#ffff] text-xs sm:text-sm tracking-[0] leading-5">
            FAQ
          </span>
        </Badge>

        <h2 className="font-normal font-['SharpGroteskPeTrialBook-20',sans-serif] text-[36px] sm:text-[56px] leading-[34px] text-3xl sm:text-4xl md:text-5xl lg:text-[70px] text-center tracking-[-1.68px] lg:leading-[120%] px-4">
          <span className="tracking-[-0.94px] bg-[radial-gradient(38.63%_62.02%_at_43.72%_20.95%,#155dfc_0%,#FFF_100%)] bg-clip-text text-transparent">Your Burning </span>
         
<br/>
              <span className="font-normal text-white">
                Questions, Answered:
              </span> 
          
        </h2>

        <p className="max-w-[848px] font-inter tracking-[-1.5px] text-[#A1A1AA] text-base sm:text-lg md:text-[18px] text-center leading-6 md:leading-7 font-[300] px-4">
          We know Hong Kong payment setups can sound like a black box. That’s why we’ve answered the most important questions so you can explore how MuteTaxes keeps your payments stable, compliant, and future-ready.
        </p>
      </header>

      {/* Desktop & Tablet: Two columns */}
      <div className="hidden md:flex items-start w-full gap-6 lg:gap-10 xl:gap-14">
        {/* Left Column */}
        <div className="flex flex-col flex-1 gap-4">
          <Accordion type="single" collapsible className="flex flex-col gap-4">
            {leftColumnFaqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="flex flex-col items-start gap-4 px-4 md:px-6 py-2 rounded-xl overflow-hidden border border-solid border-[#0f1218] shadow-[0px_16px_32px_-12px_rgba(14,18,27,0.1)] data-[state=open]:bg-transparent"
              >
                <AccordionTrigger className="flex items-center gap-3 md:gap-4 w-full hover:no-underline [&[data-state=open]>.plus-icon]:hidden [&[data-state=closed]>.minus-icon]:hidden">
                  <span className="flex-1 text-left font-inter text-[#ffffff] text-sm md:text-base tracking-[-0.32px] leading-6 md:leading-7 font-normal whitespace-pre-line">
                    {faq.question}
                  </span>
                  <PlusIcon className="plus-icon w-4 h-4 md:w-5 md:h-5 text-white flex-shrink-0" />
                  <MinusIcon className="minus-icon w-4 h-4 md:w-5 md:h-5 text-white flex-shrink-0" />
                </AccordionTrigger>

                <AccordionContent>
                  <p className="text-xs md:text-sm text-zinc-400 leading-5 md:leading-6">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Right Column */}
        <div className="flex flex-col flex-1 gap-4">
          <Accordion type="single" collapsible className="flex w-full flex-col gap-4">
            {rightColumnFaqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="flex flex-col items-start gap-4 px-4 md:px-6 py-2 rounded-xl overflow-hidden border border-solid border-[#0f1218] shadow-[0px_16px_32px_-12px_rgba(14,18,27,0.1)] data-[state=open]:bg-transparent"
              >
                <AccordionTrigger className="flex items-center gap-3 md:gap-4 w-full hover:no-underline [&[data-state=open]>.plus-icon]:hidden [&[data-state=closed]>.minus-icon]:hidden">
                  <span className="flex-1 w-full text-left font-inter text-[#ffffff] text-sm md:text-base tracking-[-0.32px] leading-6 md:leading-7 font-normal whitespace-pre-line">
                    {faq.question}
                  </span>
                  <PlusIcon className="plus-icon w-4 h-4 md:w-5 md:h-5 text-white flex-shrink-0" />
                  <MinusIcon className="minus-icon w-4 h-4 md:w-5 md:h-5 text-white flex-shrink-0" />
                </AccordionTrigger>

                <AccordionContent>
                  <p className="text-xs md:text-sm text-zinc-400 leading-5 md:leading-6">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Mobile: Single column with all FAQs */}
      <div className="flex md:hidden flex-col w-full gap-4">
        <Accordion type="single" collapsible className="flex flex-col gap-4">
          {allFaqs.map((faq) => (
            <AccordionItem
              key={faq.id}
              value={faq.id}
              className="flex flex-col items-start gap-4 px-4 py-2 rounded-xl overflow-hidden border border-solid border-[#0f1218] shadow-[0px_16px_32px_-12px_rgba(14,18,27,0.1)] data-[state=open]:bg-transparent"
            >
              <AccordionTrigger className="flex items-center gap-3 w-full hover:no-underline [&[data-state=open]>.plus-icon]:hidden [&[data-state=closed]>.minus-icon]:hidden">
                <span className="flex-1 text-left font-inter text-[#ffffff] text-sm tracking-[-0.32px] leading-6 font-normal whitespace-pre-line">
                  {faq.question}
                </span>
                <PlusIcon className="plus-icon w-4 h-4 text-white flex-shrink-0" />
                <MinusIcon className="minus-icon w-4 h-4 text-white flex-shrink-0" />
              </AccordionTrigger>

              <AccordionContent>
                <p className="text-xs text-zinc-400 leading-5">
                  {faq.answer}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
